import { useState, useEffect } from 'react'
import { Calendar, Users, Heart, DollarSign, TrendingUp, Clock } from 'lucide-react'
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore'
import { db, COLLECTIONS } from '../firebase/config'
import { useNavigate } from 'react-router-dom'

const Dashboard = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [revenueData, setRevenueData] = useState([]) // monthly (last 5 months)
  const [dailyRevenueData, setDailyRevenueData] = useState([]) // last 7 days
  const [stats, setStats] = useState([
    { title: 'Total Bookings', value: '0', change: '0%', changeType: 'positive', icon: Calendar, color: 'text-blue-600' },
    { title: 'Active Customers', value: '0', change: '0', changeType: 'positive', icon: Users, color: 'text-green-600' },
    { title: 'Services Offered', value: '0', change: '0', changeType: 'positive', icon: Heart, color: 'text-purple-600' },
    { title: 'Monthly Revenue', value: 'GHS 0', change: '0%', changeType: 'positive', icon: DollarSign, color: 'text-emerald-600' }
  ])
  const [recentBookings, setRecentBookings] = useState([])

  // Helper function to get start and end of month
  const getMonthRange = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1)
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999)
    return { start, end }
  }

  // Helper function to format date for Firestore query
  const formatDateForQuery = (date) => {
    return date.toISOString().split('T')[0]
  }

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const now = new Date()
      const currentMonth = getMonthRange(now)
      const lastMonth = getMonthRange(new Date(now.getFullYear(), now.getMonth() - 1, 1))

      // Fetch all bookings
      const bookingsRef = collection(db, COLLECTIONS.BOOKINGS)
      const bookingsSnapshot = await getDocs(bookingsRef)
      const allBookings = bookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // Fetch manual payments
      const manualPaymentsRef = collection(db, COLLECTIONS.MANUAL_PAYMENTS)
      const manualPaymentsSnapshot = await getDocs(manualPaymentsRef)
      const manualPayments = manualPaymentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // Fetch gift card purchases
      const giftCardsRef = collection(db, COLLECTIONS.GIFT_CARDS)
      const giftCardsSnapshot = await getDocs(giftCardsRef)
      const giftCards = giftCardsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // Calculate total bookings
      const totalBookings = allBookings.length

      // Filter bookings for current month
      const currentMonthBookings = allBookings.filter(booking => {
        if (!booking.date) return false
        const bookingDate = new Date(booking.date)
        return bookingDate >= currentMonth.start && bookingDate <= currentMonth.end
      })

      // Filter bookings for last month
      const lastMonthBookings = allBookings.filter(booking => {
        if (!booking.date) return false
        const bookingDate = new Date(booking.date)
        return bookingDate >= lastMonth.start && bookingDate <= lastMonth.end
      })

      // Calculate booking change
      const bookingChange = lastMonthBookings.length > 0
        ? (((currentMonthBookings.length - lastMonthBookings.length) / lastMonthBookings.length) * 100).toFixed(0)
        : currentMonthBookings.length > 0 ? '100' : '0'

      // Helper to normalize Firestore timestamps / strings to JS Date
      const getDateValue = (value) => {
        if (!value) return null
        // Firestore Timestamp has a toDate method
        if (typeof value.toDate === 'function') {
          return value.toDate()
        }
        return new Date(value)
      }

      // Calculate monthly revenue (deposits + completed balance payments) from bookings
      const calculateRevenue = (bookings) => {
        return bookings.reduce((total, booking) => {
          let revenue = 0
          if (booking.deposited && booking.deposit) {
            revenue += parseFloat(booking.deposit) || 0
          }
          if (booking.balancePaid && booking.paymentCompleted && booking.balance) {
            revenue += parseFloat(booking.balance) || 0
          }
          return total + revenue
        }, 0)
      }

      const currentMonthBookingRevenue = calculateRevenue(currentMonthBookings)
      const lastMonthBookingRevenue = calculateRevenue(lastMonthBookings)

      // Manual payments revenue (completed only)
      const getManualPaymentDate = (p) => getDateValue(p.date || p.createdAt)
      const currentMonthManualRevenue = manualPayments.reduce((sum, p) => {
        if (p.status && p.status !== 'completed') return sum
        const d = getManualPaymentDate(p)
        if (!d || d < currentMonth.start || d > currentMonth.end) return sum
        return sum + (Number(p.amount) || 0)
      }, 0)
      const lastMonthManualRevenue = manualPayments.reduce((sum, p) => {
        if (p.status && p.status !== 'completed') return sum
        const d = getManualPaymentDate(p)
        if (!d || d < lastMonth.start || d > lastMonth.end) return sum
        return sum + (Number(p.amount) || 0)
      }, 0)

      // Gift card purchase revenue
      const getGiftCardDate = (card) => getDateValue(card.dateCreated || card.createdAt)
      const currentMonthGiftRevenue = giftCards.reduce((sum, card) => {
        const d = getGiftCardDate(card)
        if (!d || d < currentMonth.start || d > currentMonth.end) return sum
        return sum + (Number(card.amount) || 0)
      }, 0)
      const lastMonthGiftRevenue = giftCards.reduce((sum, card) => {
        const d = getGiftCardDate(card)
        if (!d || d < lastMonth.start || d > lastMonth.end) return sum
        return sum + (Number(card.amount) || 0)
      }, 0)

      const currentMonthRevenue = currentMonthBookingRevenue + currentMonthManualRevenue + currentMonthGiftRevenue
      const lastMonthRevenue = lastMonthBookingRevenue + lastMonthManualRevenue + lastMonthGiftRevenue

      const revenueChange = lastMonthRevenue > 0
        ? (((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100).toFixed(0)
        : currentMonthRevenue > 0 ? '100' : '0'

      // Fetch customers
      const customersRef = collection(db, COLLECTIONS.CUSTOMERS)
      const customersSnapshot = await getDocs(customersRef)
      const totalCustomers = customersSnapshot.docs.length

      // Calculate customers from last month (approximate - count bookings from last month with unique phones)
      const lastMonthUniquePhones = new Set(
        lastMonthBookings.map(b => b.phone).filter(Boolean)
      ).size
      const currentMonthUniquePhones = new Set(
        currentMonthBookings.map(b => b.phone).filter(Boolean)
      ).size
      const customerChange = lastMonthUniquePhones > 0
        ? (((currentMonthUniquePhones - lastMonthUniquePhones) / lastMonthUniquePhones) * 100).toFixed(0)
        : currentMonthUniquePhones > 0 ? '+' + currentMonthUniquePhones : '0'

      // Fetch services (both regular and combo packages)
      const servicesRef = collection(db, COLLECTIONS.SERVICES)
      const servicesSnapshot = await getDocs(query(servicesRef, where('isActive', '==', true)))
      const comboRef = collection(db, COLLECTIONS.COMBO_PACKAGES)
      const comboSnapshot = await getDocs(query(comboRef, where('isActive', '==', true)))
      
      const totalServices = servicesSnapshot.docs.length + comboSnapshot.docs.length
      
      // Get previous services count (approximate - fetch all and count active)
      const allServicesRef = collection(db, COLLECTIONS.SERVICES)
      const allServicesSnapshot = await getDocs(allServicesRef)
      const allCombosRef = collection(db, COLLECTIONS.COMBO_PACKAGES)
      const allCombosSnapshot = await getDocs(allCombosRef)
      
      // For services change, we'll use a simple count comparison
      // This is approximate since we don't track historical data
      const servicesChange = '+0' // Can't calculate accurately without historical data

      // Get recent bookings (last 5, ordered by date descending)
      const sortedBookings = [...allBookings].sort((a, b) => {
        const dateA = new Date(a.date || 0)
        const dateB = new Date(b.date || 0)
        if (dateA.getTime() === dateB.getTime()) {
          // If same date, sort by time
          const timeA = a.time || '00:00'
          const timeB = b.time || '00:00'
          return timeB.localeCompare(timeA)
        }
        return dateB.getTime() - dateA.getTime()
      })

      const recent = sortedBookings.slice(0, 5).map(booking => ({
        id: booking.id,
        customer: booking.name || 'Unknown',
        service: booking.serviceType || booking.category || 'Service',
        date: booking.date || 'N/A',
        time: booking.time || 'N/A',
        status: booking.status || 'pending'
      }))

      // Format revenue
      const formatRevenue = (amount) => {
        return `GHS ${amount.toFixed(2)}`
      }
      
      // Apply payment provider fee deduction (2.95%) to displayed revenue
      const applyFee = (amount) => {
        const feeRate = 0.0295
        return amount * (1 - feeRate)
      }

      // Build monthly revenue series for last 5 months (bookings + manual + gift cards)
      const months = []
      for (let i = 4; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const start = new Date(date.getFullYear(), date.getMonth(), 1)
        const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999)
        months.push({
          label: date.toLocaleString('default', { month: 'short' }),
          start,
          end
        })
      }

      const monthlyRevenueSeries = months.map((month) => {
        const monthBookings = allBookings.filter(booking => {
          if (!booking.date) return false
          const bookingDate = new Date(booking.date)
          return bookingDate >= month.start && bookingDate <= month.end
        })
        const bookingRevenue = calculateRevenue(monthBookings)

        const manualRevenue = manualPayments.reduce((sum, p) => {
          if (p.status && p.status !== 'completed') return sum
          const d = getManualPaymentDate(p)
          if (!d || d < month.start || d > month.end) return sum
          return sum + (Number(p.amount) || 0)
        }, 0)

        const giftRevenue = giftCards.reduce((sum, card) => {
          const d = getGiftCardDate(card)
          if (!d || d < month.start || d > month.end) return sum
          return sum + (Number(card.amount) || 0)
        }, 0)

        const total = bookingRevenue + manualRevenue + giftRevenue
        return {
          label: month.label,
          value: applyFee(total)
        }
      })

      // Build daily revenue series for last 7 days
      const days = []
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i)
        const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
        const end = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)

        const dayBookings = allBookings.filter(booking => {
          if (!booking.date) return false
          const bookingDate = new Date(booking.date)
          return bookingDate >= start && bookingDate <= end
        })
        const dayBookingRevenue = calculateRevenue(dayBookings)

        const dayManualRevenue = manualPayments.reduce((sum, p) => {
          if (p.status && p.status !== 'completed') return sum
          const d = getManualPaymentDate(p)
          if (!d || d < start || d > end) return sum
          return sum + (Number(p.amount) || 0)
        }, 0)

        const dayGiftRevenue = giftCards.reduce((sum, card) => {
          const d = getGiftCardDate(card)
          if (!d || d < start || d > end) return sum
          return sum + (Number(card.amount) || 0)
        }, 0)

        const total = dayBookingRevenue + dayManualRevenue + dayGiftRevenue
        days.push({
          label: date.toLocaleDateString('en-US', { weekday: 'short' }),
          value: applyFee(total)
        })
      }

      // Update stats
      setStats([
        {
          title: 'Total Bookings',
          value: totalBookings.toString(),
          change: `${parseFloat(bookingChange) > 0 ? '+' : ''}${bookingChange}%`,
          changeType: parseFloat(bookingChange) >= 0 ? 'positive' : 'negative',
          icon: Calendar,
          color: 'text-blue-600'
        },
        {
          title: 'Active Customers',
          value: totalCustomers.toString(),
          change: `${parseFloat(customerChange) > 0 ? '+' : ''}${customerChange}%`,
          changeType: parseFloat(customerChange) >= 0 ? 'positive' : 'negative',
          icon: Users,
          color: 'text-green-600'
        },
        {
          title: 'Services Offered',
          value: totalServices.toString(),
          change: servicesChange,
          changeType: 'positive',
          icon: Heart,
          color: 'text-purple-600'
        },
        {
          title: 'Monthly Revenue',
          // Display net revenue after 2.95% Paystack fee deduction
          value: formatRevenue(applyFee(currentMonthRevenue)),
          change: `${parseFloat(revenueChange) > 0 ? '+' : ''}${revenueChange}%`,
          changeType: parseFloat(revenueChange) >= 0 ? 'positive' : 'negative',
          icon: DollarSign,
          color: 'text-emerald-600'
        }
      ])

      // Prepare revenue chart data (net amounts)
      setRevenueData(monthlyRevenueSeries)
      setDailyRevenueData(days)

      setRecentBookings(recent)
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return dateString
    }
  }

  const formatTime = (timeString) => {
    if (!timeString || timeString === 'N/A') return 'N/A'
    try {
      const [hours, minutes] = timeString.split(':')
      const hour = parseInt(hours)
      const ampm = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour % 12 || 12
      return `${displayHour}:${minutes} ${ampm}`
    } catch {
      return timeString
    }
  }

  return (
    <div className="space-y-6 text-white">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-slate-900/80 bg-opacity-70 backdrop-blur-md rounded-xl border border-purple-800/40 p-6 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                {loading ? (
                  <>
                    <div className="h-3 w-24 bg-slate-800/80 rounded mb-3 animate-pulse" />
                    <div className="h-6 w-20 bg-slate-700/80 rounded mb-2 animate-pulse" />
                    <div className="h-3 w-28 bg-slate-800/80 rounded animate-pulse" />
                  </>
                ) : (
                  <>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{stat.title}</p>
                    <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                    <p
                      className={`text-sm mt-1 ${
                        stat.changeType === 'positive' ? 'text-emerald-400' : 'text-rose-400'
                      }`}
                    >
                      {stat.change} from last month
                    </p>
                  </>
                )}
              </div>
              <div className={`p-3 rounded-lg bg-slate-900/90 ${stat.color}`}>
                <stat.icon className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Charts */}
        <div className="space-y-6">
          {/* Monthly Revenue (last 5 months) */}
          <div className="bg-slate-900/80 bg-opacity-70 backdrop-blur-md rounded-xl border border-purple-800/40 p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-white mb-2">Revenue Overview (Last 5 Months)</h3>
            <p className="text-xs text-gray-400 mb-4">
              Includes bookings, manual balance payments, and gift card purchases (net after fees).
            </p>
            <div className="h-64 bg-slate-950/70 rounded-lg flex items-center justify-center border border-slate-800/80">
              {loading ? (
                <div className="w-3/4 h-32 flex items-end justify-between gap-3 px-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex flex-col items-center space-y-2 w-full">
                      <div className="w-full bg-slate-800/70 rounded-md animate-pulse" style={{ height: `${20 + i * 6}px` }} />
                      <div className="h-2 w-8 bg-slate-800/80 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : revenueData && revenueData.length > 0 ? (
                <div className="w-full h-full flex flex-col items-center justify-center">
                  <svg className="w-full h-full p-4" viewBox="0 0 200 100" preserveAspectRatio="none">
                    {(() => {
                      const max = Math.max(...revenueData.map((d) => d.value), 1)
                      const barWidth = 20
                      const gap = 12
                      const totalWidth = revenueData.length * barWidth + (revenueData.length - 1) * gap
                      const startX = (200 - totalWidth) / 2
                      return revenueData.map((d, i) => {
                        const height = (d.value / max) * 70
                        const x = startX + i * (barWidth + gap)
                        const y = 80 - height
                        const isLatest = i === revenueData.length - 1
                        const fill = isLatest ? '#ec4899' : '#6366f1'
                        return (
                          <g key={d.label}>
                            <rect x={x} y={y} width={barWidth} height={height} rx="4" fill={fill} />
                            <text
                              x={x + barWidth / 2}
                              y={95}
                              fontSize="6"
                              fill="#cbd5e1"
                              textAnchor="middle"
                            >
                              {d.label}
                            </text>
                            <text
                              x={x + barWidth / 2}
                              y={y - 4}
                              fontSize="6"
                              fill="#e2e8f0"
                              textAnchor="middle"
                            >
                              {`GHS ${d.value.toFixed(0)}`}
                            </text>
                          </g>
                        )
                      })
                    })()}
                  </svg>
                </div>
              ) : (
                <div className="text-center px-4">
                  <TrendingUp className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">No revenue data available yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* Daily Revenue Trend (last 7 days) */}
          <div className="bg-slate-900/80 bg-opacity-70 backdrop-blur-md rounded-xl border border-purple-800/40 p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-white mb-2">Daily Revenue Trend (Last 7 Days)</h3>
            <p className="text-xs text-gray-400 mb-4">
              Line chart showing combined daily revenue with a smooth trend across days.
            </p>
            <div className="h-64 bg-slate-950/70 rounded-lg flex items-center justify-center border border-slate-800/80">
              {loading ? (
                <div className="w-3/4 h-32 flex flex-col justify-between px-4 py-2">
                  <div className="h-1 w-full bg-slate-800/70 rounded-full animate-pulse" />
                  <div className="flex justify-between mt-4">
                    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                      <div key={i} className="flex flex-col items-center space-y-2">
                        <div className="h-8 w-1 bg-slate-800/80 rounded-full animate-pulse" />
                        <div className="h-2 w-6 bg-slate-800/80 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : dailyRevenueData && dailyRevenueData.length > 0 ? (
                <div className="w-full h-full flex flex-col items-center justify-center">
                  <svg className="w-full h-full p-4" viewBox="0 0 200 100" preserveAspectRatio="none">
                    {(() => {
                      const max = Math.max(...dailyRevenueData.map((d) => d.value), 1)
                      const minY = 20
                      const maxY = 80
                      const points = dailyRevenueData.map((d, i) => {
                        const t =
                          dailyRevenueData.length === 1 ? 0.5 : i / (dailyRevenueData.length - 1)
                        const x = 10 + t * 180
                        const y = maxY - (d.value / max) * (maxY - minY)
                        return { x, y, label: d.label, value: d.value }
                      })

                      const pathD = points
                        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
                        .join(' ')

                      return (
                        <>
                          {/* Baseline */}
                          <line
                            x1="10"
                            y1={maxY}
                            x2="190"
                            y2={maxY}
                            stroke="#1f2937"
                            strokeWidth="1"
                          />
                          {/* Trendline */}
                          <path
                            d={pathD}
                            fill="none"
                            stroke="#ec4899"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          {/* Points */}
                          {points.map((p, i) => (
                            <g key={p.label + i}>
                              <circle
                                cx={p.x}
                                cy={p.y}
                                r="3"
                                fill="#f9a8d4"
                                stroke="#111827"
                                strokeWidth="1"
                              />
                              <text
                                x={p.x}
                                y={p.y - 6}
                                fontSize="6"
                                fill="#e5e7eb"
                                textAnchor="middle"
                              >
                                {`GHS ${p.value.toFixed(0)}`}
                              </text>
                              <text
                                x={p.x}
                                y={maxY + 8}
                                fontSize="6"
                                fill="#9ca3af"
                                textAnchor="middle"
                              >
                                {p.label}
                              </text>
                            </g>
                          ))}
                        </>
                      )
                    })()}
                  </svg>
                </div>
              ) : (
                <div className="text-center px-4">
                  <Clock className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">No recent daily revenue data available yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-slate-900/80 bg-opacity-70 backdrop-blur-md rounded-xl border border-purple-800/40 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Recent Bookings</h3>
            <button
              onClick={() => navigate('/bookings')}
              className="text-sm text-purple-400 hover:text-purple-300 font-medium transition-colors"
            >
              View All
            </button>
          </div>
          <div className="space-y-4">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 bg-slate-950/70 rounded-lg border border-slate-800/80"
                  >
                    <div className="space-y-2">
                      <div className="h-3 w-24 bg-slate-800/80 rounded animate-pulse" />
                      <div className="h-3 w-32 bg-slate-800/80 rounded animate-pulse" />
                    </div>
                    <div className="text-right space-y-2">
                      <div className="h-3 w-20 bg-slate-800/80 rounded animate-pulse ml-auto" />
                      <div className="h-3 w-16 bg-slate-800/80 rounded animate-pulse ml-auto" />
                      <div className="h-5 w-16 bg-slate-800/80 rounded-full animate-pulse ml-auto" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentBookings.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-400">No bookings yet</p>
              </div>
            ) : (
              recentBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="flex items-center justify-between p-3 bg-slate-950/70 rounded-lg hover:bg-slate-900/80 transition-colors cursor-pointer border border-slate-800/80"
                  onClick={() => navigate('/bookings')}
                >
                  <div>
                    <p className="font-medium text-white">{booking.customer}</p>
                    <p className="text-sm text-gray-300">{booking.service}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-white">{formatDate(booking.date)}</p>
                    <p className="text-sm text-gray-300">{formatTime(booking.time)}</p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                        booking.status === 'completed'
                          ? 'bg-emerald-900/80 text-emerald-300'
                          : booking.status === 'rescheduled'
                          ? 'bg-blue-900/80 text-blue-300'
                          : 'bg-slate-800/80 text-gray-300'
                      }`}
                    >
                      {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-900/80 bg-opacity-70 backdrop-blur-md rounded-xl border border-purple-800/40 p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/bookings')}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white rounded-lg transition-colors font-medium shadow-md shadow-purple-900/40"
          >
            <Calendar className="w-5 h-5" />
            <span>View Bookings</span>
          </button>
          <button 
            onClick={() => navigate('/customers')}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-slate-950/70 hover:bg-slate-900/80 text-white rounded-lg transition-colors font-medium border border-slate-800/80"
          >
            <Users className="w-5 h-5" />
            <span>Manage Customers</span>
          </button>
          <button 
            onClick={() => navigate('/services')}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-slate-950/70 hover:bg-slate-900/80 text-white rounded-lg transition-colors font-medium border border-slate-800/80"
          >
            <Heart className="w-5 h-5" />
            <span>Manage Services</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
